import { Typography, Tooltip } from '@mui/material';
import { useMiseSellerGroups, useMiseSellers } from '@/lib/query/hooks/useMiseAccounts';
import { buildComparisonPath } from '@/lib/mise/comparisonPath';
import type { AppDbUserSettings } from '@/lib/types/mise/user';

interface UserComparisonCellProps {
  /** 階層を引くアカウント（ユーザーの既定アカウント） */
  accountId: string;
  settings: AppDbUserSettings;
}

const KIND_LABEL = { seller: '店舗', seller_group: '店舗グループ' } as const;

/**
 * ユーザー一覧の「比較店舗」セル。
 * 同名の店舗・店舗グループ（ブランドごとの「関東」など）を見分けられるよう、比較先から上位への階層で表示する。
 */
export function UserComparisonCell({ accountId, settings }: UserComparisonCellProps) {
  // 同じアカウントのユーザーが並んでも、取得はアカウントごとに 1 回（TanStack Query のキャッシュ）
  const { data: sellers = [] } = useMiseSellers(accountId);
  const { data: groups = [] } = useMiseSellerGroups(accountId);

  const comparison = buildComparisonPath(settings, sellers, groups);
  if (!comparison) {
    return <Typography color="text.secondary">未設定</Typography>;
  }

  // 最上位（全社など）はどのユーザーも同じで見分けに使えないため出さない（比較先そのものが最上位なら出す）。
  // 比較先から上位へ向けて並べ、列に収まらないときは見分けに要らない上位の側が省略されるようにする
  const shown = comparison.path.length > 1 ? comparison.path.slice(1) : comparison.path;
  const [target, ...ancestors] = [...shown].reverse();
  return (
    <Tooltip title={`${KIND_LABEL[comparison.kind]}：${comparison.path.join(' › ')}`}>
      <Typography
        variant="body2"
        sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        <Typography component="span" variant="caption" color="text.secondary">
          {KIND_LABEL[comparison.kind]}：
        </Typography>
        <Typography component="span" variant="body2" fontWeight="bold">
          {target}
        </Typography>
        {ancestors.length > 0 && (
          <Typography component="span" variant="body2" color="text.secondary">
            {` ‹ ${ancestors.join(' ‹ ')}`}
          </Typography>
        )}
      </Typography>
    </Tooltip>
  );
}
